/**
 * OPPGAVER GIS TIL PERSONER, IKKE TIL BAND (217.8).
 *
 * Velgerne for ansvarlig, medansvarlige og personfilteret leser kontaktlista, og i den står aktene
 * — «Aurora Kollektivet», kontakten en programpost står på som artist. Kontaktlista har ingen
 * markering for det, så `GET /v1/tasks/unassignable` sier hvilke personer som er akter, og
 * velgerne trekker dem fra. API-et avviser dem som ansvarlig uansett (`act_not_person`).
 *
 * Bandmedlemmene er egne kontakter (0375) og blir stående: det er dem en oppgave gis til.
 */
import { taskUnassignableResponseSchema, type TaskUnassignableResponse } from '@tvitvi/shared';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

export function taskUnassignableQueryOptions(orgId: string) {
  return queryOptions({
    queryKey: qk.taskUnassignable(orgId),
    queryFn: ({ signal }): Promise<TaskUnassignableResponse> =>
      apiRequest({ path: '/tasks/unassignable', schema: taskUnassignableResponseSchema, signal }),
    ...CACHE.reference,
  });
}

/** Personene som ikke kan få en oppgave. Tom mens svaret ikke er kommet — lista står da som før. */
export function useUnassignable(orgId: string, enabled = true): ReadonlySet<string> {
  const query = useQuery({ ...taskUnassignableQueryOptions(orgId), enabled });
  return useMemo(() => new Set(query.data?.personIds ?? []), [query.data]);
}

/** Radene uten aktene. */
export function withoutActs<T extends { personId: string | null }>(
  rows: readonly T[],
  acts: ReadonlySet<string>,
): T[] {
  return rows.filter((row) => row.personId === null || !acts.has(row.personId));
}
